import moment from "moment";
import Card from "@mui/material/Card";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Forum from "@mui/icons-material/Forum";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import TaskAltOutlined from "@mui/icons-material/TaskAltOutlined";
import { Box, DataTable, useCommonComponentContext } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Screens from "constants/screens";
import Strings from "constants/strings";
import Constants, { USER_ROLE } from "constants/index";
import useDataCommon from "hooks/useDataCommon.hook";
import TagService from "services/medkit/tag.service";
import ReportService from "services/medkit/report.service";
import ModalRepeatIssueReport from "./ModalRepeatIssueReport";

import { RootState } from "store";
import { DashboardLayout } from "layout";
import { BoxValueFilter, FilterForm } from "./FormFilter";
import { ICodename, IRecordReport } from "commons/interfaces";
import { setListPathName } from "store/slice/titleRoute.slice";
import { BooleanInt, PL, RepeatIssue, ReportStatus, Status } from "constants/enum";
import { setDataAlert, showLoading, showLoadingDataTable } from "store/slice/common.slice";
import FishboneModal from "screens/statistic/fishbone";



const reportService = new ReportService();
const tagService = new TagService();

export interface IDataFilterReport {
    endDate?: string | number,
    startDate?: string | number,

    status?: string,
    levelId?: string,
    targetId?: string,
    searchText?: string,
    byHospitalId?: string,
    departmentId?: string,
    tagIds?: string[],
    isRepeatIssues?: BooleanInt,
    anonymousReporter?: BooleanInt;

    pageSize?: number;
    pageNumber?: number;
    totalCount?: number;
};

const ReportScreen = () => {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { getAllDepartments } = useDataCommon();
    const { clientId, userInfo } = useCommonComponentContext();

    const dataMapCodenames = useSelector((state: RootState) => state.common.codenames);
    const loadingDataTable = useSelector((state: RootState) => state.common.loadingDataTable);
    const userAuthorization = useSelector((state: RootState) => state.user.userAuthorization);

    const [searchParams] = useSearchParams();
    const [dataGroups, setDataGroups] = useState<ICodename[]>([]);
    const [dataTags, setDataTags] = useState<ICodename[]>([]);
    const [dataFilter, setDataFilter] = useState<IDataFilterReport>({});
    const [dataFilterTemp, setDataFilterTemp] = useState<IDataFilterReport>({});
    const [open, setOpen] = useState<boolean>(false);
    const [description, setDescription] = useState<string>("");
    const [id, setId] = useState<string>("");

    const [dataReports, setDataReports] = useState<IRecordReport[]>([]);
    const [dataModalRepeatIssueReport, setDataModalRepeatIssueReport] = useState<{ open?: boolean, data?: IRecordReport }>({});

    const pageSize = searchParams.get("size");
    const pageNumber = searchParams.get("page");
    const endDate = searchParams.get("to");
    const startDate = searchParams.get("from");
    const searchText = searchParams.get("text");
    const reportStatus = searchParams.get("status");
    const levelId = searchParams.get("level");
    const targetId = searchParams.get("target");
    const byHospitalId = searchParams.get("byHospital");
    const departmentId = searchParams.get("department");
    const tagIds = searchParams.get("tags");
    const isRepeatIssues = searchParams.get("isRepeatIssues");
    const anonymousReporter = searchParams.get("anonymousReporter");

    useEffect(() => {
        (async () => {
            try {
                const result = await getAllDepartments({
                    clientId: clientId,
                    organizationId: userInfo?.userProfile?.organizationId,
                });
                setDataGroups(result);
            } catch (error) { }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const result = await tagService.getAll();
                const items: ICodename[] = [...result || []].map(element => ({
                    code: element.id,
                    name: element.tagName,
                }));
                setDataTags(items);
            } catch (error) { }
        })();
    }, []);

    useEffect(() => {
        const size = Number(pageSize || Constants.ROW_PER_PAGE);
        const page = Number(pageNumber || Constants.PAGE_DEFAULT);

        let newEndDate: any = moment().endOf("y").unix();
        let newStartDate: any = moment().startOf("y").unix();
        if (!Helpers.isNullOrEmpty(endDate) && (endDate !== "0")) {
            newEndDate = endDate;
        };
        if (!Helpers.isNullOrEmpty(startDate) && (startDate !== "0")) {
            newStartDate = startDate;
        };

        const newReq: IDataFilterReport = {
            endDate: newEndDate,
            startDate: newStartDate,
            searchText: searchText,
            status: reportStatus,
            levelId: levelId,
            targetId: targetId,
            byHospitalId: byHospitalId,
            departmentId: departmentId,
            tagIds: !Helpers.isNullOrEmpty(tagIds) ? tagIds.split(",") : undefined,
            pageSize: size,
            pageNumber: page,
        };

        if (!Helpers.isNullOrEmpty(isRepeatIssues)) {
            newReq.isRepeatIssues = Number(isRepeatIssues);
        }

        if (!Helpers.isNullOrEmpty(anonymousReporter) && Number(anonymousReporter) === BooleanInt.True) {
            newReq.anonymousReporter = BooleanInt.True;
        }

        loadDataReports(newReq);
    }, [
        pageSize,
        pageNumber,
        endDate,
        startDate,
        searchText,
        levelId,
        targetId,
        departmentId,
        tagIds,
        byHospitalId,
        reportStatus,
        isRepeatIssues,
        anonymousReporter
    ]);

    const loadDataReports = async (newReq: IDataFilterReport) => {
        try {
            dispatch(showLoadingDataTable(true));

            let onlyOpenedReport: any = undefined;
           
            const result = await reportService.getPaged({
                endTime: newReq.endDate,
                startTime: newReq.startDate,
                reportStatus: newReq.status,
                searchText: newReq.searchText,
                byHospitals: newReq.byHospitalId,
                incidentObject: newReq.targetId,
                firstAffectValuation: newReq.levelId,
                incidentPlaces: newReq.departmentId ? [newReq.departmentId] : [],
                tagIds: newReq.tagIds,
                repeatIssues : newReq.isRepeatIssues == BooleanInt.True ? [RepeatIssue.RepeatedUnconfirmed, RepeatIssue.Repeated] : [],
                anonymousReporter: newReq.anonymousReporter,
                onlyOpenedReport,
                pageSize: newReq.pageSize,
                pageNumber: newReq.pageNumber,
            });

            setDataFilter(prev => ({ ...prev, ...newReq, totalCount: result?.totalCount || 0 }));
            setDataFilterTemp(prev => ({ ...prev, ...newReq, totalCount: result?.totalCount || 0 }));
            setDataReports([...result?.items || []]);

        } catch (error) {
            const err = Helpers.handleExceptionError(error);
            !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
        } finally {
            dispatch(showLoadingDataTable(false));
        }
    };

    const handleFilterData = (req: IDataFilterReport) => {
        let query: string = `?page=${req.pageNumber || Constants.PAGE_DEFAULT}&size=${req.pageSize || Constants.ROW_PER_PAGE}`;

        if (!Helpers.isNullOrEmpty(req.startDate)) {
            query += `&from=${req.startDate}`;
        } else {
            query += `&from=${moment().startOf("y").unix()}`;
        }

        if (!Helpers.isNullOrEmpty(req.endDate)) {
            query += `&to=${req.endDate}`;
        } else {
            query += `&to=${moment().endOf("y").unix()}`;
        }

        if (!Helpers.isNullOrEmpty(req.searchText)) {
            query += `&text=${req.searchText}`;
        }
        if (!Helpers.isNullOrEmpty(req.status)) {
            query += `&status=${req.status}`;
        }
        if (!Helpers.isNullOrEmpty(req.departmentId)) {
            query += `&department=${req.departmentId}`;
        }
        if (!Helpers.isNullOrEmpty(req.targetId)) {
            query += `&target=${req.targetId}`;
        }
        if (!Helpers.isNullOrEmpty(req.levelId)) {
            query += `&level=${req.levelId}`;
        }
        if (!Helpers.isNullOrEmpty(req.byHospitalId)) {
            query += `&byHospital=${req.byHospitalId}`;
        }
        if (!Helpers.isNullOrEmpty(req.tagIds) && (req.tagIds?.length || 0) > 0) {
            query += `&tags=${req.tagIds?.join(",")}`;
        }
        if (!Helpers.isNullOrEmpty(req.isRepeatIssues)) {
            query += `&isRepeatIssues=${req.isRepeatIssues}`;
        }
        if (!Helpers.isNullOrEmpty(req.anonymousReporter)) {
            query += `&anonymousReporter=${req.anonymousReporter}`;
        }

        navigate(Screens.REPORT + query);
        dispatch(setListPathName({ pathname: Screens.REPORT, query }));
    };

    const handleCloseReport = (id: string) => {
        Helpers.showConfirmAlert(
            Strings.REPORT.CONFIRM_CLOSE_REPORT,
            async () => {
                try {
                    dispatch(showLoadingDataTable(true));

                    await reportService.updateStatus({
                        id: id,
                        reportStatus: ReportStatus.closed,
                    });

                    await loadDataReports({ ...dataFilter });

                    dispatch(setDataAlert({ message: Strings.REPORT.CLOSE_REPORT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoadingDataTable(false));
                }
            },
            () => { }
        );
    };

    const handleReopenReport = async (id: string) => {
        Helpers.showConfirmAlert(
            Strings.REPORT.CONFIRM_REOPEN_REPORT,
            async () => {
                try {
                    dispatch(showLoadingDataTable(true));

                    await reportService.updateStatus({
                        id: id,
                        reportStatus: ReportStatus.new,
                    });

                    await loadDataReports({ ...dataFilter });

                    dispatch(setDataAlert({ message: Strings.REPORT.REOPEN_REPORT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoadingDataTable(false));
                }
            },
            () => { }
        );
    };

    const handlePrintReport = async (id: string) => {
        Helpers.showConfirmAlert(
            Strings.REPORT.CONFIRM_PRINT_REPORT,
            async () => {
                try {
                    dispatch(showLoading(true));

                    const resultPLIII = await reportService.generatingPL({
                        id: id,
                        reportType: PL.PLIII
                    });

                    const resultPLIV = await reportService.generatingPL({
                        id: id,
                        reportType: PL.PLIV
                    });

                    var fileNamePLIII = `${id}_PLIII.pdf`;
                    const reponseBlobPLIII = new Blob([resultPLIII]);

                    const downloadUrlPLIII = URL.createObjectURL(reponseBlobPLIII);

                    const aPLIII = document.createElement("a");

                    aPLIII.href = downloadUrlPLIII;
                    aPLIII.download = fileNamePLIII;
                    document.body.appendChild(aPLIII);
                    aPLIII.click();

                    //

                    var fileNamePLIV = `${id}_PLIV.pdf`;
                    const reponseBlobPLIV = new Blob([resultPLIV]);

                    const downloadUrlPLIV = URL.createObjectURL(reponseBlobPLIV);

                    const aPLIV = document.createElement("a");

                    aPLIV.href = downloadUrlPLIV;
                    aPLIV.download = fileNamePLIV;
                    document.body.appendChild(aPLIV);
                    aPLIV.click();

                    // Clean up
                    window.URL.revokeObjectURL(downloadUrlPLIII);
                    window.URL.revokeObjectURL(downloadUrlPLIV);
                    document.body.removeChild(aPLIV);
                    document.body.removeChild(aPLIII);

                    dispatch(setDataAlert({ message: Strings.REPORT.PRINT_REPORT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoading(false));
                }
            },
            () => { }
        );
    };

    const handleDeleteReport = (id: string) => {
        Helpers.showConfirmAlert(
            Strings.REPORT.CONFIRM_DELETE_REPORT,
            async () => {
                try {
                    dispatch(showLoadingDataTable(true));

                    await reportService.delete(id);

                    await loadDataReports({ ...dataFilter });

                    dispatch(setDataAlert({ message: Strings.REPORT.DELETE_REPORT_SUCCESS, type: "success" }));
                } catch (error) {
                    const err = Helpers.handleExceptionError(error);
                    !Helpers.isNullOrEmpty(err) && dispatch(setDataAlert({ message: err, type: "error" }));
                } finally {
                    dispatch(showLoadingDataTable(false));
                }
            },
            () => { },

        );
    };

    const dataColumns = useMemo(() => ([
        {
            Header: Strings.COMMON.STATUS,
            accessor: "reportStatus",
            width: "80px",
            Cell: ({ value }: any) => {
                const itemStatus = [...dataMapCodenames?.reportStatus || []][Number(value)];
                const colorStatus = Constants.Styles.STATUS_COLOR[Number(value)] || "black";
                return <Box sx={{ color: colorStatus }}>{itemStatus?.name}</Box>
            },
        },
        {
            Header: Strings.REPORT.REPORT_CODE,
            accessor: "reportNo",
            width: "120px",
        },
        {
            Header: Strings.REPORT.DESCRIPTION,
            accessor: "shortDescription",
            Cell: ({ value }: any) => {
                return <span title={value}>{value}</span>
            }
        },
        {
            Header: Strings.REPORT.TYPE_REPORT,
            accessor: "reportType",
            width: "80px",
            Cell: ({ value }: any) => {
                const itemType = [...dataMapCodenames?.reportType || []][Number(value)];
                return <>{itemType?.name}</>
            },
        },
        {
            Header: Strings.REPORT.TARGET_OBJECT,
            accessor: "incidentObject",
            Cell: ({ value }: any) => {
                const itemTarget = [...dataMapCodenames?.incidentObject || []][Number(value)];
                return <>{itemTarget?.name}</>
            },
        },
        {
            Header: Strings.REPORT.CREATE_TIME_REPORT,
            accessor: "createTime",
            width: "100px",
            Cell: ({ value }: any) => {
                return <>{Helpers.dateToString(Number(value), Constants.DateFormat.DDMMYYYY_HHMM)}</>
            },
        },
        {
            Header: Strings.REPORT.REPORTER,
            accessor: "reporterName",
            Cell: (row: any) => {
                const report = row?.row?.original as IRecordReport;
                const reporterName = report?.anonymousReporter === BooleanInt.True ? Strings.REPORT.ANONYMOUS_REPORT : report.reporterName;
                return <>{reporterName}</>;
            },
        },
        {
            Header: Strings.REPORT.REPEAT_ISSUE,
            accessor: "repeatIssue",
            width: "50px",
            Cell: ({ value }: any) => {
                return (Number(value || BooleanInt.False) === RepeatIssue.None) ? <RadioButtonUnchecked />  :  <TaskAltOutlined /> 
            },
        },
        {
            Header: Strings.REPORT.REPORT_COUNT,
            accessor: "reportCount",
            width: "80px"
        },
    ]), [
        dataMapCodenames?.reportType,
        dataMapCodenames?.reportStatus,
        dataMapCodenames?.incidentObject,
    ]);

    const dataRows = useMemo(() => [...dataReports || []], [dataReports])

    return (
        <DashboardLayout
            isPermission
            requiredOrganization
            title={Strings.REPORT.LIST_REPORT}
            route={[{ title: Strings.REPORT.LIST_REPORT, route: "" }]}
            listActions={[
                {
                    title: Strings.REPORT.INCIDENT_REPORT,
                    onPress: () => { navigate(`${Screens.REPORT}${Screens.REPORT_INCIDENT}`); },
                }
            ]}
        >
            <Box>
                <BoxValueFilter
                    dataGroups={dataGroups}
                    dataTags={dataTags}
                    dataFilter={dataFilter}
                    dataMapCodenames={dataMapCodenames}
                    handleFilterData={handleFilterData}
                />
                <Card sx={{ padding: 2 }}>
                    <DataTable
                        isShowIndex
                        loading={loadingDataTable}
                        totalCount={dataFilter?.totalCount || 0}
                        pageNumber={dataFilter?.pageNumber || 1}
                        rowPerPage={dataFilter?.pageSize || Constants.ROW_PER_PAGE}
                        onChangePageSize={pageSize => handleFilterData({ ...dataFilter, pageSize })}
                        onChangePageNumber={pageNumber => handleFilterData({ ...dataFilter, pageNumber })}

                        table={{ columns: dataColumns, rows: dataRows }}

                        filterForm={
                            <FilterForm
                                dataGroups={dataGroups}
                                dataTags={dataTags}
                                dataFilterTemp={dataFilterTemp}
                                dataMapCodenames={dataMapCodenames}
                                setDataFilterTemp={setDataFilterTemp}
                            />
                        }
                        onCloseFilter={() => { setDataFilterTemp({ ...dataFilter }) }}
                        onReset={() => { handleFilterData({ pageNumber: Constants.PAGE_DEFAULT }); }}
                        onFilter={() => { handleFilterData({ ...dataFilterTemp, pageNumber: Constants.PAGE_DEFAULT }) }}

                        searchText={dataFilter?.searchText}
                        placeholderSearch={Strings.REPORT.SEARCH_TEXT}
                        onSearch={(value) => {
                            handleFilterData({
                                ...dataFilter,
                                searchText: value,
                                pageNumber: Constants.PAGE_DEFAULT,
                            });
                        }}
                        onSearchChange={(value) => {
                            setDataFilter(prev => ({
                                ...prev,
                                searchText: value,
                                pageNumber: Constants.PAGE_DEFAULT,
                            }));
                        }}

                        actionList={(row) => {
                            const isAdmin = userAuthorization?.userAuthorizationResponse?.some(userAuthRes => userAuthRes.roleCode === USER_ROLE.ADMIN);

                            if (row.reportStatus === ReportStatus.closed) {
                                if (isAdmin) {
                                    // dropdownAdminRpClosed
                                    return ([
                                        {
                                            key: "chat",
                                            icon: <Forum />,
                                            title: "Chat",
                                            callback: (row) => {
                                                Helpers.showAlert(Strings.MESSAGE.NO_FUNCTION);
                                            },
                                        },
                                        {
                                            key: "edit",
                                            iconName: "edit",
                                            title: Strings.COMMON.EDIT,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.REPORT}${Screens.REPORT_INCIDENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "analysis",
                                            title: Strings.ANALYSIS.ANALYSIS,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.REPORT}${Screens.REPORT_ANALYSIS}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "reopen",
                                            title: Strings.REPORT.REOPEN_REPORT,
                                            callback: (row) => { handleReopenReport(row.id); },
                                        },
                                        {
                                            key: "repeat",
                                            title: Strings.REPORT.REPEAT_ISSUE,
                                            callback: (row) => {
                                                setDataModalRepeatIssueReport({ open: true, data: row });
                                            },
                                        },
                                        {
                                            key: "print",
                                            title: Strings.REPORT.PRINT_REPORT,
                                            callback: (row) => { handlePrintReport(row.id); },
                                        },
                                        {
                                            key: "delete",
                                            title: Strings.COMMON.DELETE,
                                            callback: (row) => { handleDeleteReport(row.id); },
                                        },
                                    ]);
                                } else {
                                    // dropdownView
                                    return ([
                                        {
                                            key: "chat",
                                            icon: <Forum />,
                                            title: "Chat",
                                            callback: (row) => {
                                                Helpers.showAlert(Strings.MESSAGE.NO_FUNCTION);
                                            },
                                        },
                                        {
                                            key: "view",
                                            iconName: "view",
                                            title: Strings.COMMON.VIEW_DETAIL,
                                            callback: (row) => {
                                                navigate(`${Screens.REPORT}${Screens.REPORT_INCIDENT}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "view_analysis",
                                            title: Strings.ANALYSIS.VIEW_ANALYSIS,
                                            callback: (row) => {
                                                navigate(`${Screens.REPORT}${Screens.REPORT_ANALYSIS}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "print",
                                            title: Strings.REPORT.PRINT_REPORT,
                                            callback: (row) => { handlePrintReport(row.id); },
                                        },
                                    ]);
                                };
                            } else {
                                if (isAdmin) {
                                    // dropdownAdmin
                                    return ([
                                        {
                                            key: "chat",
                                            icon: <Forum />,
                                            title: "Chat",
                                            callback: (row) => {
                                                Helpers.showAlert(Strings.MESSAGE.NO_FUNCTION);
                                            },
                                        },
                                        {
                                            key: "edit",
                                            iconName: "edit",
                                            title: Strings.COMMON.EDIT,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.REPORT}${Screens.REPORT_INCIDENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "fisbone",
                                            title: Strings.ANALYSIS.AI_ANALYSIS,
                                            callback: (row) => {
                                                console.log("row.id", row.id);
                                                setId(row.id);
                                                setDescription(row.shortDescription || "");
                                                setOpen(true);
                                            },
                                        },
                                        {
                                            key: "analysis",
                                            title: Strings.ANALYSIS.ANALYSIS,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.REPORT}${Screens.REPORT_ANALYSIS}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "repeat",
                                            title: Strings.REPORT.REPEAT_ISSUE,
                                            callback: (row) => {
                                                setDataModalRepeatIssueReport({ open: true, data: row });
                                            },
                                        },
                                        {
                                            key: "close",
                                            title: Strings.REPORT.CLOSE_REPORT,
                                            callback: (row) => { handleCloseReport(row.id); },
                                        },
                                        {
                                            key: "print",
                                            title: Strings.REPORT.PRINT_REPORT,
                                            callback: (row) => { handlePrintReport(row.id); },
                                        },
                                        {
                                            key: "delete",
                                            title: Strings.COMMON.DELETE,
                                            callback: (row) => { handleDeleteReport(row.id); },
                                        },
                                    ])
                                } else {
                                    // dropdownReporter
                                    return ([
                                        {
                                            key: "chat",
                                            icon: <Forum />,
                                            title: "Chat",
                                            callback: (row) => {
                                                Helpers.showAlert(Strings.MESSAGE.NO_FUNCTION);
                                            },
                                        },
                                        {
                                            key: "edit",
                                            iconName: "edit",
                                            title: Strings.COMMON.EDIT,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.REPORT}${Screens.REPORT_INCIDENT}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "analysis",
                                            title: Strings.ANALYSIS.ANALYSIS,
                                            callback: (row) => {
                                                const nextScreen = `${Screens.REPORT}${Screens.REPORT_ANALYSIS}`;
                                                navigate(`${nextScreen}?id=${row.id}`);
                                            },
                                        },
                                        {
                                            key: "repeat",
                                            title: Strings.REPORT.REPEAT_ISSUE,
                                            callback: (row) => {
                                                setDataModalRepeatIssueReport({ open: true, data: row });
                                            },
                                        },
                                        {
                                            key: "print",
                                            title: Strings.REPORT.PRINT_REPORT,
                                            callback: (row) => { handlePrintReport(row.id); },
                                        },
                                    ]);
                                };
                            }
                        }}
                    />
                </Card>
                <ModalRepeatIssueReport
                    data={dataModalRepeatIssueReport?.data}
                    visible={dataModalRepeatIssueReport?.open || false}
                    dataOptionsReportType={[...dataMapCodenames?.reportType || []]}
                    dataOptionsReportStatus={[...dataMapCodenames?.reportStatus || []]}
                    onClose={() => {
                        setDataModalRepeatIssueReport({});
                    }}
                    onSubmit={() => {
                        setDataModalRepeatIssueReport({});
                        loadDataReports({ ...dataFilter });
                    }}
                />
                <FishboneModal setOpen={setOpen} open={open} input={description} id={id}/>
            </Box>
        </DashboardLayout>
    );
};

export default ReportScreen;
